import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { orgApi, spaceUrl } from "../../lib/paths.ts";
import { useAction } from "../../lib/resource.ts";
import { navigate } from "../../lib/router.ts";
import { loadMe } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";
import { timeZone } from "../../lib/zone.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Field, Form, Input } from "../../ui/form.tsx";
import { LINK } from "../../ui/styles.ts";

/** A new space of `space`'s organization (by its name; or from an export), then into it. */
export function NewSpaceDialog({ space, open, onClose }: { space: SpaceOut; open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title={t("settings.newSpace")}>
      <NewSpaceForm space={space} onClose={onClose} />
    </Dialog>
  );
}

function NewSpaceForm({ space, onClose }: { space: SpaceOut; onClose: () => void }) {
  const [name, setName] = useState("");
  const [create, busy] = useAction(async () => {
    const made = await api.post<SpaceOut>(orgApi(space, "/spaces"), { name, time_zone: timeZone.value });
    await loadMe();
    toast(t("settings.spaceCreated", { name: made.name }));
    onClose();
    navigate(spaceUrl(made, "/triage"));
  });
  return (
    <Form class="flex flex-col gap-4" onSubmit={create}>
      <p class="text-sm text-zinc-600">{t("settings.spacesHint")}</p>
      <Field label={t("settings.name")}>
        <Input value={name} onValue={setName} required={true} autofocus={true} />
      </Field>
      <Link href={spaceUrl(space, "/settings/import?new=1")} class={`${LINK} text-sm`} onClick={onClose}>
        {t("import.newSpace")}
      </Link>
      <DialogActions>
        <Button answer="cancel" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy}>
          {t("common.create")}
        </Button>
      </DialogActions>
    </Form>
  );
}
